import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { isEqual } from 'lodash-es';
import isEmpty from 'lodash-es/isEmpty';
import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { SingleValueFormInput } from '../../../../helpers/single-value-form-input';
import { FormSelectOption } from '../../../entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { ShFormGroupComponent } from '../../_core/form-group/form-group.component';
import { ShFormLabelComponent } from '../../_core/form-label/form-label.component';
import { ShFormSelectBaseComponent } from '../../form-select/base/form-select-base.component';
import { DefaultEDTFTypeTranslations } from '../entities/default-edtf-type-translations.constant';
import { EDTFTypePlaceholders } from '../entities/edtf-type-placeholders.constant';
import { EDTFTypeTranslations, EDTFTypeTranslationsPartial } from '../entities/edtf-type-translations.entity';
import { EDTFType } from '../entities/edtf-type.enum';
import { detectEDTFType, isValidEDTF } from '../helpers/edtf-validation.helper';

@Component({
    selector: 'sh-form-edtf-complex-base',
    templateUrl: './form-edtf-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormEDTFComplexBaseComponent)],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        ShFormGroupComponent,
        ShFormLabelComponent,
        ShFormSelectBaseComponent,
        NgIf,
        AsyncPipe
    ]
})
export class ShFormEDTFComplexBaseComponent extends SingleValueFormInput<string | null> implements OnInit, OnDestroy {
    private readonly edtfTypeLabels$: BehaviorSubject<EDTFTypeTranslations> = new BehaviorSubject<EDTFTypeTranslations>(
        DefaultEDTFTypeTranslations
    );

    public edtfTypeOptions$!: Observable<FormSelectOption<EDTFType>[]>;
    public disableValueControl$!: Observable<boolean>;
    public typePlaceholderMsg$!: Observable<string>;
    public valuePlaceholder$!: Observable<string>;
    public indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public combinedErrorMessage$!: Observable<string | null>;

    public valueDisabled = false;
    public edtfFormGroup!: UntypedFormGroup;

    @Input() id!: string;
    @Input() formControl!: UntypedFormControl;
    @Input() required = false;
    @Input() invalid = false;
    @Input() typePlaceholderMsg = '';
    @Input() typeLabel = '';
    @Input() valueLabel = '';
    @Input() formatExamplePrefix = 'Ex.';
    @Input() indeterminateMsg = '';
    @Input() unrecognizedFormatMsg = '';
    @Input() invalidFormatMsg = '';
    @Input() requiredFieldMsg = '';

    @Input() set i18n(customTranslations: EDTFTypeTranslationsPartial) {
        if (customTranslations) {
            const combinedLabels: EDTFTypeTranslations = {
                ...DefaultEDTFTypeTranslations,
                ...customTranslations
            };

            this.edtfTypeLabels$.next(combinedLabels);
        }
    }

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super(cd);
    }

    ngOnInit(): void {
        this.initFormGroup();
        this.initEDTFTypeOptionsStream();
        this.initTypePlaceholderStream();
        this.initValuePlaceholderStream();
        this.initErrorMessageStream();

        this.initPropagation();

        this.initTypeControlListener();
        this.initValueControlListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.indeterminate$.complete();
        this.edtfTypeLabels$.complete();
    }

    private initFormGroup(): void {
        this.edtfFormGroup = this.formBuilder.group({
            type: [],
            value: []
        });
    }

    private initEDTFTypeOptionsStream(): void {
        this.edtfTypeOptions$ = this.edtfTypeLabels$.pipe(map(labels => this.getEDTFTypeOptions(labels)));
    }

    private getEDTFTypeOptions(labels: EDTFTypeTranslations): FormSelectOption<EDTFType>[] {
        const options: FormSelectOption<EDTFType>[] = Object.keys(EDTFType)
            .map(edtfKey => ({
                label: labels[edtfKey as EDTFType] ?? edtfKey,
                value: edtfKey as EDTFType
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return options;
    }

    private initValuePlaceholderStream(): void {
        const typeFormatExamplePlaceholder$: Observable<string> = this.typeControl.valueChanges.pipe(
            map(edtfType => EDTFTypePlaceholders[edtfType as EDTFType]),
            map(example => (example ? `${this.formatExamplePrefix.trim()} ${example}` : '')),
            startWith('')
        );

        this.valuePlaceholder$ = combineLatest([this.indeterminate$, typeFormatExamplePlaceholder$]).pipe(
            map(([indeterminate, typeFormatExamplePlaceholder]) =>
                indeterminate ? this.indeterminateMsg : typeFormatExamplePlaceholder
            )
        );
    }

    private initTypePlaceholderStream(): void {
        this.typePlaceholderMsg$ = this.indeterminate$.pipe(
            map(indeterminate => (indeterminate ? this.indeterminateMsg : this.typePlaceholderMsg))
        );
    }

    private initErrorMessageStream(): void {
        this.combinedErrorMessage$ = this.formControl.statusChanges.pipe(
            map(status =>
                status === 'INVALID' ? (this.formControl.getError('message') ?? this.requiredFieldMsg) : null
            )
        );
    }

    private initTypeControlListener(): void {
        const changes$: Observable<any> = this.typeControl.valueChanges;

        changes$.pipe(startWith(null), takeUntil(this.destroy$)).subscribe(value => {
            if (value) {
                this.valueDisabled = false;
            } else {
                this.valueDisabled = true;
                this.valueControl.setValue('');
            }
        });

        const firstChange$: Observable<any> = changes$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChange$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));
    }

    private initValueControlListener(): void {
        const changes$: Observable<any> = this.valueControl.valueChanges;

        const firstChange$: Observable<any> = changes$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChange$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));

        combineLatest([this.typeControl.valueChanges, changes$, this.valuePlaceholder$])
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(([type, value, valuePlaceholder]) => {
                if (!Object.values(EDTFType).includes(type)) {
                    this.propagateChange(new FormValue(null, FormValueState.UNSET));
                    return;
                }

                if (!value || (!isEmpty(value) && !isValidEDTF(value, type))) {
                    this.propagateChange(new FormValue(null, FormValueState.UNSET));
                    this.formControl.setErrors({ message: `${this.invalidFormatMsg} ${valuePlaceholder}` });
                } else {
                    this.propagateChange(new FormValue(value, FormValueState.SET));
                }
            });
    }

    private initPropagation(): void {
        combineLatest([this.valueControl.valueChanges, this.indeterminate$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([value, indeterminate]) => {
                if (indeterminate) {
                    return this.propagateChange(new FormValue('', FormValueState.INDETERMINATE));
                }

                // Don't propagate if no value set
                if (value && !isEmpty(value)) {
                    return this.propagateChange(new FormValue(value, FormValueState.SET));
                }
            });
    }

    get typeControl(): UntypedFormControl {
        return this.edtfFormGroup.get('type') as UntypedFormControl;
    }

    get valueControl(): UntypedFormControl {
        return this.edtfFormGroup.get('value') as UntypedFormControl;
    }

    get typeControlId(): string {
        return `${this.id}-type`;
    }

    get valueControlId(): string {
        return `${this.id}-value`;
    }

    public unset(): void {
        this.indeterminate$.next(false);

        this.valueControl.setValue(null);
    }

    public set(value: string | null): void {
        if (!value) {
            console.error('ShFormEDTFComplexBaseComponent:: FormValue does not contain a valid EDTF date', value);

            this.unset();

            return;
        }

        const edtfType: EDTFType | null = detectEDTFType(value);

        this.indeterminate$.next(false);

        if (!edtfType) {
            this.setUnrecognized();
        } else {
            this.typeControl.setValue(edtfType);
        }

        this.valueControl.setValue(value);
    }

    private setUnrecognized() {
        this.typeControl.setValue(this.unrecognizedFormatMsg);
        this.valueDisabled = true;
    }

    public makeIndeterminate(): void {
        this.indeterminate$.next(true);

        this.valueControl.setValue(null);
    }

    setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);

        if (isDisabled) {
            this.typeControl.disable();
            this.valueControl.disable();
        } else {
            this.typeControl.enable();
            this.valueControl.enable();
        }
    }
}
